import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as FormAction from '../store/form';
import * as apiUtil from '../../../util/apiUtil';
import Loading from '../../../component/loading/Loading';
import FormListTable from '../component/FormListTable';
import FormSearchEngine from '../component/FormSearchEngine';
import GoogleConnectView from '../component/GoogleConnectView';

const FormListContainer = () => {
    const dispatch = useDispatch();
    const { formList, page, size, total } = useSelector(state => state.form);

    const [loading, setLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [requestKeyword, setRequestKeyword] = useState('');
    const [user, setUser] = useState(null);

    // 사용자 정보 조회
    useEffect(() => {
        apiUtil.asyncRequest({
            url: "/api/bizchat/user/info",
            method: apiUtil.requestMethod.POST,
            success: (result) => {
                setUser(result);
            }
        });
    }, []);

    // 폼 목록 조회
    const fetchFormList = useCallback(() => {
        if (!user || !user.googleEmail) return;

        apiUtil.asyncRequest({
            setLoading,
            url: '/api/user/forms/list',
            method: apiUtil.requestMethod.POST,
            data: {
                page,
                size,
                search: requestKeyword,
            },
            success: (result) => {
                dispatch(FormAction.setFormList(result.content));
                dispatch(FormAction.setTotal(result.total));
            }
        });
    }, [page, size, requestKeyword, dispatch, user]);

    // 페이지 변경 시 자동 조회
    useEffect(() => {
        fetchFormList();
    }, [fetchFormList]);

    // 페이지 변경 핸들러
    const handlePageChange = (event, newPage) => {
        dispatch(FormAction.setPage(newPage));
    };

    // 검색 핸들러
    const handleSearch = () => {
        dispatch(FormAction.setPage(1)); // 검색 시 첫 페이지로
        setRequestKeyword(searchKeyword);
    };

    // 검색어 입력 핸들러
    const handleSearchChange = (value) => {
        setSearchKeyword(value);
    };

    // Enter 키 핸들러
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // 폼 삭제 핸들러
    const handleDelete = (formInstanceId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) {
            return;
        }

        apiUtil.asyncRequest({
            setLoading,
            url: `/api/user/forms/${formInstanceId}`,
            method: apiUtil.requestMethod.DELETE,
            success: () => {
                alert('삭제되었습니다.');
                fetchFormList();
            }
        });
    };

    if (!user) {
        return <Loading loading={true} />;
    }

    if (!user.googleEmail) {
        return (
            <>
                <Loading loading={loading} />
                <GoogleConnectView />
            </>
        );
    }

    return (
        <>
            <Loading loading={loading} />
            <div className='container-xxl'>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className='fs-4 mb-0'>설문 관리</h2>
                    <div className="d-flex align-items-center gap-3">
                        <div className="text-muted small">
                            연동된 계정: {user.googleEmail}
                        </div>
                        <a href="/form/create" className="btn btn-primary">
                            + 새 폼 만들기
                        </a>
                    </div>
                </div>

                <div className="mx-auto mb-4" style={{ maxWidth: '800px' }}>
                    <FormSearchEngine
                        searchKeyword={searchKeyword}
                        onSearchChange={handleSearchChange}
                        onSearch={handleSearch}
                        onKeyDown={handleKeyDown}
                    />
                </div>

                <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
                    <i className="bi bi-info-circle-fill me-2 mt-1"></i>
                    <div>
                        아래 응답 수는 캠페인 발송 후 7일 동안 자동으로 집계된 결과입니다.
                        상세 응답 내용 및 이후 집계는 연동된 구글 계정의 <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer" className="alert-link">Google Drive</a>에서 직접 확인하실 수 있습니다.
                    </div>
                </div>

                <div className='card'>
                    <div className='card-body'>
                        <FormListTable
                            formList={formList}
                            total={total}
                            page={page}
                            size={size}
                            onPageChange={handlePageChange}
                            onDelete={handleDelete}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default FormListContainer;
